import * as React from "react";
import { useDispatch } from "@teamprevent/admin-redux";

import { Button, Confirm, Dropdown, DropdownOnSearchChangeData, Grid, Icon, Table } from "semantic-ui-react";

import { getIdentityValidationOptionsValue, ImmunisationListModel } from "@teamprevent/common/immunisation/models";
import { CaseType } from "@teamprevent/common/model";

import { Customer } from "../../crud/customer";
import { EmployeeRecord } from "../../crud/employeeRecord";
import { AuthorizeProducts } from "../../auth/AuthorizeProducts";
import { loadEmployeeImmunisations, sendImmsHistory as sendImmsHistoryAction } from "../actions";

import { Link } from "redux-little-router";

interface Props {
    customer: Customer;
    employeeRecord: EmployeeRecord;
    allImmunisations: ImmunisationListModel[];
    immsTypeOptions: { key: string; value: string; text: string }[];
    path: string;
}

export const AllImmunisationRecords: React.FunctionComponent<Props> = (props: Props) => {

    const { customer, employeeRecord, allImmunisations, immsTypeOptions, path } = props;

    const [showImmsHistoryConfirmation, setShowImmsHistoryConfirmation] = React.useState<boolean>(false);
    const [isDateCompletedSortOrderDesc, setIsDateCompletedSortOrderDesc] = React.useState<boolean>(true);
    const [immsTypes, setImmsTypes] = React.useState<string[]>([]);

    const dispatch = useDispatch();

    const sendImmsHistory = (customerId: string, employeeRecordId: string) => {
        dispatch(sendImmsHistoryAction({ customerId, employeeRecordId }));
    };

    const onShowSendImmsHistoryConfirmation = () => {
        setShowImmsHistoryConfirmation(true);
    };

    const onConfirmSendImmsHistory = () => {
        setShowImmsHistoryConfirmation(false);
        sendImmsHistory(customer.id, employeeRecord.id);
    };

    const onCancelSendImmsHistory = () => {
        setShowImmsHistoryConfirmation(false);
    };

    const onImmsTypeSearchChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownOnSearchChangeData) => {
        const immsTypeIds = data.value as string[];
        setImmsTypes(immsTypeIds);
    };

    const filteredImmunisations = (!immsTypes || immsTypes.length === 0)
        ? allImmunisations
        : allImmunisations.filter(i => immsTypes?.includes(i.immunisationType.id));
    const immunisations = filteredImmunisations.sort((a, b) => {
        if (a.dateCompleted) {
            if (isDateCompletedSortOrderDesc) {
                return -a.dateCompleted.diff(b.dateCompleted);
            }

            return a.dateCompleted.diff(b.dateCompleted);
        }

        return -1;
    });

    React.useEffect(() => {
        if (employeeRecord) {
            dispatch(loadEmployeeImmunisations({ employeeRecordId: employeeRecord.id }));
        }
    }, [dispatch, employeeRecord]);

    return (
        <>
            <Grid container className="nomargintop">
                <Grid.Row >
                    <Grid.Column width={6}>
                        <h1>Immunisations</h1>
                    </Grid.Column>
                    <Grid.Column width={10} textAlign="right">
                        <Button
                            content="Send Imms History"
                            onClick={onShowSendImmsHistoryConfirmation}
                        />
                        <Button content="Print" as={Link} href={`${path}/immunisationRecord`} target="_blank" rel="noopener noreferrer" />
                        <AuthorizeProducts customer={customer} productTypes={CaseType.IMMUNISATION}>
                            <Button content="Create Immunisation case" as={Link} href={`${path}/createImmunisationCase`} />
                        </AuthorizeProducts>
                        <Button icon="add" content="Create" as={Link} href={`${path}/create`} />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Immunisation</Table.HeaderCell>
                                    <Table.HeaderCell>
                                        Date complete<Icon name="sort" onClick={() => setIsDateCompletedSortOrderDesc(!isDateCompletedSortOrderDesc)} />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Type</Table.HeaderCell>
                                    <Table.HeaderCell>Outcome</Table.HeaderCell>
                                    <Table.HeaderCell>IVS Sample</Table.HeaderCell>
                                    <Table.HeaderCell>Completed by</Table.HeaderCell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <Dropdown
                                            fluid
                                            multiple
                                            selection
                                            value={immsTypes}
                                            onChange={onImmsTypeSearchChange}
                                            clearable
                                            options={immsTypeOptions}
                                            search
                                            placeholder="Search..."
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {immunisations && immunisations.map(immunisation => (
                                    <Table.Row key={immunisation.id}>
                                        <Table.Cell>
                                            <Link href={`${path}/${immunisation.id}`}>
                                                {immunisation.immunisationType.name}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {immunisation.dateCompleted && immunisation.dateCompleted.format("DD/MM/YYYY")}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {immunisation.recordType}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {immunisation.outcome}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {getIdentityValidationOptionsValue(immunisation.identityValidation)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {immunisation.completedByName}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Confirm
                header="Send Imms History"
                open={showImmsHistoryConfirmation}
                content={`You are sending this immunisation history to the following email address ${employeeRecord?.personalEmailAddress}. `
                    + "Do you wish to continue?"}
                cancelButton="Cancel"
                confirmButton="Yes"
                onCancel={onCancelSendImmsHistory}
                onConfirm={onConfirmSendImmsHistory}
            />
        </>
    );
};
